import type { InvestmentSummary, MetricParameters, PortfolioMetricTypes } from "@eu.mdotm.api/sphere";
import { Divider } from "$root/components/Divider";
import { reapplySphereMetricsWithConfirmationDialog } from "$root/functional-areas/portfolio/metrics";
import { useLocaleFormatters } from "$root/localization/hooks";
import {
	PortfolioQueryWidgetBase,
	type WidgetStatus,
	type WidgetStatusUnion,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { type Maybe, qualifier } from "$root/utils";
import { useQueryNoRefetch, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import { IconButton } from "@mdotm/shadcn/mdotm/components";
import {
	type OrderBy,
	type StylableProps,
	type TableColumn,
	ActionText,
	AutoSortHScrollTable,
	CircularProgressBar,
	Column,
	IconTooltip,
	Row,
	Svg,
	Text,
	TooltipContent,
} from "@mdotm/shadcn/legacy/components";
import { useAsync, type MaybePromise } from "@mdotm/utils/promise";
import { type NodeOrFn, renderNodeOrFn, useDebounced } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { builtInSortFnFor, unpromisify } from "@mdotm/shadcn/legacy/utils";
import type { QueryKey } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sandwich } from "@mdotm/shadcn/mdotm/layout";
import type { UnitOfMeasurement } from "$root/functional-areas/metrics";
import { type ColumnMetadata, spawnColumnChooser } from "$root/ui-lib/table/extra/ColumnChooser";
import { Map } from "immutable";
import { noop } from "@mdotm/utils/runtime";
import { MonetaryUnderPercent } from "./MonetaryUnderPercent";

export type PortfolioMetricsRow = {
	name: string;
	visible: boolean;
	label: string;
	classificationUuid?: string;
	type?: PortfolioMetricTypes;
	unitOfMeasurement: UnitOfMeasurement;
	decimalPlaces?: number;
	current: Maybe<number>;
	benchmark: Maybe<number>;
	proposal?: Maybe<number>;
	isCurrentCustom?: Maybe<boolean>;
	isBenchmarkCustom?: Maybe<boolean>;
	metricParameters?: MetricParameters;
	/** Backend-supplied monetary value of the percentage, shown under it when the table has `formatMonetary`. */
	monetary?: { current?: number; proposal?: number };
};

export type PortfolioMetricsBaseProps = {
	hasBenchmark: boolean;
	title: NodeOrFn<{ lastImportDate?: string; hasCustomMetrics?: boolean }>;
	metricsProvider: () => MaybePromise<
		| {
				data: { metrics: Array<PortfolioMetricsRow>; lastImportDate?: string; hasCustomMetrics?: boolean };
				widgetStatus: WidgetStatus.READY;
		  }
		| {
				data: undefined;
				widgetStatus: Exclude<WidgetStatusUnion, "ready">;
		  }
	>;
	saveHandler?: (data: Array<PortfolioMetricsRow>) => Promise<void>;
	/** Extra action-header content (e.g. a download button), rendered before the Settings button once data is ready. */
	actionHeader?: NodeOrFn;
	noDataText?: string;
	queryKey: QueryKey;
	proposalDate: string | undefined;
	/**
	 * Controlled table sort. When provided, the table's sort becomes driven by the parent — used to mirror
	 * the user's column sort elsewhere (e.g. in the widget's data export). Left undefined the table sorts
	 * itself internally (uncontrolled), unchanged.
	 */
	orderBy?: OrderBy[];
	onOrderByChange?: (orderBy: OrderBy[]) => void;
	/**
	 * Whether the metrics query may run at all, on top of the benchmark it already requires. The as-of
	 * view closes it while the snapshot being read is not available yet, because the backend answers 404
	 * for a snapshot it is still computing (MDOT-24860). Absent means open.
	 */
	enabled?: boolean;
	/** Handed to the table: renders a row's `monetary` value under its percentage. */
	formatMonetary?: (value: number) => string;
};

export function PortfolioMetricsBase({
	title,
	metricsProvider,
	queryKey,
	hasBenchmark,
	saveHandler,
	actionHeader,
	enabled,
	...props
}: PortfolioMetricsBaseProps): React.JSX.Element {
	const enableRowQuery = hasBenchmark && (enabled ?? true);
	const rowsQuery = useQueryNoRefetch({
		enabled: enableRowQuery,
		queryKey,
		queryFn: metricsProvider,
	});

	const { invoke: debouncedSave } = useDebounced(
		(x) => {
			if (saveHandler) {
				saveHandler(x)
					.then(() => rowsQuery.refetch())
					.catch(noop);
			}
		},
		{ debounceInterval: 1000 },
	);

	useQueryReactor(rowsQuery, {
		onSuccess({ data }) {
			setCachedRows(data?.metrics ?? []);
		},
	});

	const rows = rowsQuery.data?.data?.metrics;
	const rowsMap = useMemo(() => {
		if (!rows) {
			return undefined;
		}
		return Map(rows.map((m) => [m.name, m]));
	}, [rows]);

	useWidgetOptions(
		() => ({
			isLoading: rowsQuery.isLoading,
			title: renderNodeOrFn(title, {
				lastImportDate: rowsQuery.data?.data?.lastImportDate,
				hasCustomMetrics: rowsQuery.data?.data?.hasCustomMetrics,
			}),
			actionHeader: function ActionHeader() {
				const settingsButton =
					saveHandler && rowsMap && rows ? (
						<IconButton
							applyNegativeMargins
							disabled={rowsQuery.isLoading}
							onClick={() =>
								spawnColumnChooser({
									title: "Portfolio metrics Settings",
									description: "Manage metrics display options",
									columns: rows.map(
										(r): ColumnMetadata<string> => ({
											id: r.name,
											checked: r.visible,
											label: r.label,
										}),
									),
									onChange(newRows) {
										debouncedSave(
											newRows.map((newRow) => ({
												...rowsMap.get(newRow.id)!,
												visible: newRow.checked,
											})),
										);
									},
								})
							}
							icon="Settings"
							size={20}
							color={rowsQuery.isLoading ? themeCSSVars.palette_N400 : themeCSSVars.palette_N500}
						/>
					) : null;
				const extraActions = rows ? renderNodeOrFn(actionHeader) : null;
				if (!settingsButton && !extraActions) {
					return null;
				}
				return (
					<Row alignItems="center" gap={8}>
						{extraActions}
						{settingsButton}
					</Row>
				);
			},
		}),
		[title, saveHandler, actionHeader, rowsQuery.data?.data, rowsQuery.isLoading, rowsMap, debouncedSave, rows],
	);

	const [cachedRows, setCachedRows] = useState<Array<PortfolioMetricsRow>>([]);
	const visibleRows = useMemo(() => cachedRows.filter((x) => x.visible), [cachedRows]);

	return (
		<PortfolioQueryWidgetBase query={rowsQuery}>
			{() => (
				<div className="flex flex-1 flex-col relative z-0 overflow-hidden">
					<div className="relative z-0 flex flex-col flex-1 min-h-0">
						<PortfolioMetricsBaseTable {...props} rows={visibleRows} />
					</div>
					{rowsQuery.data?.data?.hasCustomMetrics && (
						<>
							<Divider />
							<Row gap={8} flexGrow={0} className="pt-1" alignItems="center">
								<WarningIndicator />
								<Text type="Body/S/Book" color={themeCSSVars.palette_N400}>
									Imported metric
								</Text>
							</Row>
						</>
					)}
				</div>
			)}
		</PortfolioQueryWidgetBase>
	);
}

export function PortfolioMetricsBaseTable({
	rows,
	noDataText,
	proposalDate,
	orderBy,
	onOrderByChange,
	formatMonetary,
}: {
	noDataText?: string;
	rows: Array<PortfolioMetricsRow>;
	proposalDate: string | undefined;
	orderBy?: OrderBy[];
	onOrderByChange?: (orderBy: OrderBy[]) => void;
	/** Renders a row's `monetary` value under its percentage. Absent, the cells show the percentage only. */
	formatMonetary?: (value: number) => string;
}): React.JSX.Element {
	const { formatNumber, formatDate } = useLocaleFormatters();
	const { t } = useTranslation();

	const showProposal = rows.some((r) => Boolean(r.proposal));

	const columns = useMemo<TableColumn<(typeof rows)[number]>[]>(
		() => [
			{
				minWidth: showProposal ? 96 : 144,
				header: t("METRICS"),
				content: ({ label }) => label,
				sortFn: builtInSortFnFor("label"),
				name: "label",
			},
			{
				width: 124,
				cellClassName: "tabular-nums",
				headerCellClassName: "whitespace-pre-line",
				header: showProposal ? `Composition\n${formatDate(proposalDate, { omitYear: true })}` : "Current",
				align: "end" as const,
				content: ({ current, isCurrentCustom, unitOfMeasurement, decimalPlaces, monetary }, cellProps) => (
					<Sandwich {...cellProps}>
						<Row
							data-qualifier={qualifier.widgets.portfolioExAnteMetric.column("Current")}
							className="inset-0 p-[inherit]"
							alignItems="center"
							justifyContent="end"
						>
							<MonetaryUnderPercent
								primary={`${formatNumber(current, decimalPlaces)}${unitOfMeasurement}`}
								secondary={monetary?.current != null ? formatMonetary?.(monetary.current) : undefined}
								data-qualifier={qualifier.widgets.portfolioExAnteMetric.monetaryValue("Current")}
							/>
						</Row>
						{isCurrentCustom && <WarningIndicator className="top-0 right-0 pointer-events-none" />}
					</Sandwich>
				),
				sortFn: builtInSortFnFor("current"),
				name: "current",
			},
			{
				width: 124,
				cellClassName: "tabular-nums",
				header: "Benchmark",
				align: "end",
				content: ({ benchmark, isBenchmarkCustom, unitOfMeasurement, decimalPlaces }, cellProps) => (
					<Sandwich {...cellProps}>
						<Row className="inset-0 p-[inherit]" alignItems="center" justifyContent="end">
							{`${formatNumber(benchmark, decimalPlaces)}${unitOfMeasurement}`}
						</Row>
						{isBenchmarkCustom && <WarningIndicator className="top-0 right-0 pointer-events-none" />}
					</Sandwich>
				),
				sortFn: builtInSortFnFor("benchmark"),
				name: "benchmark",
			},
			...(showProposal
				? [
						{
							width: 124,
							cellClassName: "tabular-nums",
							header: "Proposal",
							align: "end",
							// An element is not wrapped in the table's own cell, so it must spread `cellProps` (the
							// column's width and position) itself.
							content: ({ proposal, unitOfMeasurement, decimalPlaces, monetary }, cellProps) => (
								<Sandwich {...cellProps}>
									<Row className="inset-0 p-[inherit]" alignItems="center" justifyContent="end">
										<MonetaryUnderPercent
											primary={`${formatNumber(proposal, decimalPlaces)}${unitOfMeasurement}`}
											secondary={monetary?.proposal != null ? formatMonetary?.(monetary.proposal) : undefined}
											data-qualifier={qualifier.widgets.portfolioExAnteMetric.monetaryValue("Proposal")}
										/>
									</Row>
								</Sandwich>
							),
							sortFn: builtInSortFnFor("proposal"),
							name: "proposal",
						} satisfies TableColumn<(typeof rows)[number]>,
					]
				: []),
		],
		[proposalDate, formatDate, formatNumber, formatMonetary, showProposal, t],
	);

	return (
		<AutoSortHScrollTable
			disableVirtualScroll={window.__e2e__}
			columns={columns}
			rows={rows}
			noDataText={noDataText}
			orderBy={orderBy}
			onOrderByChange={onOrderByChange}
		/>
	);
}

function WarningIndicator(props: StylableProps) {
	return (
		<Svg {...props} viewBox={{ width: 9, height: 9 }} fill="none">
			<path d="M0 0H9V9L0 0Z" fill="#FFBE00" />
		</Svg>
	);
}

export function ReapplySphereMetricsIconTooltip(props: {
	lastUpdate: string;
	portfolio: InvestmentSummary;
}): React.JSX.Element {
	const [run, asyncState] = useAsync<void>(() => reapplySphereMetricsWithConfirmationDialog(props.portfolio));

	const { formatDateTime } = useLocaleFormatters();
	return (
		<>
			{asyncState.kind === "loading" ? (
				<CircularProgressBar value={asyncState.progress} outerDiameter={16} />
			) : (
				<IconTooltip
					severity="info"
					overrideColor={themeCSSVars.palette_N200}
					overrideIconColor={themeCSSVars.palette_N500}
					overrideIcon="Sync"
					iconSize={16}
				>
					<TooltipContent>
						<Column gap={8}>
							<Column gap={8}>
								<Text type="Body/M/Bold">Metrics have been imported</Text>
								<Text type="Body/M/Book">Last update: {formatDateTime(props.lastUpdate)}</Text>
							</Column>
							<div className="border-t border-[#D1D2DC] -mx-4" />
							<ActionText type="Body/S/Book" className="self-start" onClick={unpromisify(() => run())}>
								Reapply Sphere metrics
							</ActionText>
						</Column>
					</TooltipContent>
				</IconTooltip>
			)}
		</>
	);
}
